import { useReducedMotion, useSpring, useTransform } from 'framer-motion'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { useIconActivity } from './aida-icon-animated-activity'
import {
  AIDA_ANIMATED_FRAME_PATH,
  AIDA_EYE_LEFT_X_LOOKING,
  AIDA_EYE_LEFT_X_REST,
  AIDA_EYE_RIGHT_X_LOOKING,
  AIDA_EYE_RIGHT_X_REST,
  AIDA_GAZE_SPRING,
} from './aida-icon-animated-paths'
import {
  AnimatedEye,
  AnimatedMouth,
  AuroraFill,
  auroraGradientFill,
  GazeGroup,
} from './aida-icon-animated-parts'
import { useLivingEyes } from './aida-icon-living-eyes'

type IconProps = React.ComponentProps<'svg'>

export type AidaIconAnimatedProps = IconProps & {
  mouth?: boolean
  track?: boolean
  blink?: boolean
  aurora?: boolean
}

export function AidaIconAnimated({
  mouth = true,
  track = true,
  blink = true,
  aurora = false,
  className,
  ...props
}: AidaIconAnimatedProps) {
  const svgRef = React.useRef<SVGSVGElement>(null)
  const reduceMotion = useReducedMotion()
  const allowMotion = reduceMotion === false
  const { canHover, pageVisible, onScreen } = useIconActivity(svgRef)
  const active = allowMotion && pageVisible && onScreen
  const { gazeX, gazeY, lidScale, looking } = useLivingEyes(
    svgRef,
    track && active && canHover,
    blink && active,
  )
  const leftEyeX = useSpring(
    useTransform(looking, [0, 1], [AIDA_EYE_LEFT_X_REST, AIDA_EYE_LEFT_X_LOOKING]),
    AIDA_GAZE_SPRING,
  )
  const rightEyeX = useSpring(
    useTransform(looking, [0, 1], [AIDA_EYE_RIGHT_X_REST, AIDA_EYE_RIGHT_X_LOOKING]),
    AIDA_GAZE_SPRING,
  )
  const rawId = React.useId().replace(/:/g, '')
  const svgId = `aida-icon-${rawId}`
  const gradientId = `aida-aurora-${rawId}`
  const eyeFill = aurora ? auroraGradientFill(gradientId) : 'currentColor'

  return (
    <svg
      id={svgId}
      ref={svgRef}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={tw(className)}
      {...props}
    >
      {aurora ? <AuroraFill svgId={svgId} gradientId={gradientId} animate={allowMotion} /> : null}
      <g transform="translate(2, 2)">
        <path
          d={AIDA_ANIMATED_FRAME_PATH}
          fillRule="evenodd"
          clipRule="evenodd"
          fill="currentColor"
        />
        <GazeGroup x={gazeX} y={gazeY}>
          <AnimatedEye x={leftEyeX} lidScale={lidScale} fill={eyeFill} />
          <AnimatedEye x={rightEyeX} lidScale={lidScale} fill={eyeFill} />
        </GazeGroup>
        <AnimatedMouth visible={mouth} animate={allowMotion} />
      </g>
    </svg>
  )
}
